import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import {
  getEmployeeQueries,
  getEmployeeQueryDetails,
  submitQueryResponse,
  markQueryAsRead,
} from "../../api/queries";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { InviteModal, SuccessModal } from "../../components/modal";
import { FaCheckCircle, FaComments, FaClock } from "react-icons/fa";
import { toast } from "react-toastify";

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPriorityColor = (priority) => {
  const colors = {
    LOW: "bg-green-100 text-green-800",
    MEDIUM: "bg-yellow-100 text-yellow-800",
    HIGH: "bg-orange-100 text-orange-800",
    URGENT: "bg-red-100 text-red-800",
  };
  return colors[priority] || colors.MEDIUM;
};

const getStatusColor = (status) => {
  const colors = {
    OPEN: "bg-gray-100 text-gray-800",
    IN_PROGRESS: "bg-blue-100 text-blue-800",
    RESOLVED: "bg-green-100 text-green-800",
  };
  return colors[status] || colors.OPEN;
};

const EmployeeQueries = () => {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [responseForm, setResponseForm] = useState({
    responseText: "",
    attachmentUrl: "",
  });

  // Load employee's assigned queries
  const loadQueries = useCallback(async () => {
    try {
      setIsLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;

      const response = await getEmployeeQueries(filters);
      setQueries(response.data || response.assignments || []);
    } catch (error) {
      toast.error("Failed to load queries");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  // Load query details
  const loadQueryDetails = useCallback(async (assignmentId) => {
    try {
      setIsLoadingDetails(true);
      const response = await getEmployeeQueryDetails(assignmentId);
      const queryData = response.data || response;
      setSelectedQuery(queryData);
      
      // Mark as read if not already read
      if (queryData && queryData.id && !queryData.isRead) {
        try {
          await markQueryAsRead(queryData.id);
        } catch (e) {
          console.error("Failed to mark as read:", e);
        }
      }

      setIsDetailModalOpen(true);
    } catch (error) {
      toast.error("Failed to load query details");
      console.error(error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  // Submit response
  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!responseForm.responseText.trim()) {
      toast.error("Please enter a response");
      return;
    }

    if (!selectedQuery) return;

    try {
      setIsSubmittingResponse(true);
      await submitQueryResponse(selectedQuery.queryId || selectedQuery.id, {
        responseText: responseForm.responseText,
        attachmentUrl: responseForm.attachmentUrl || undefined,
      });

      setSuccessMessage("Your response has been submitted successfully!");
      setIsSuccessOpen(true);
      setIsResponseModalOpen(false);
      setResponseForm({ responseText: "", attachmentUrl: "" });
      setIsDetailModalOpen(false);
      await loadQueries();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to submit response"
      );
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const handleOpenDetails = (query) => {
    loadQueryDetails(query.id);
  };

  const handleOpenResponseModal = () => {
    if (!selectedQuery) return;
    setResponseForm({ responseText: "", attachmentUrl: "" });
    setIsResponseModalOpen(true);
  };

  if (isLoading) {
    return <FetchLoadingState message="Loading your queries..." />;
  }

  return (
    <div className="dashboard-enter">
      <PageIntroNav
        breadcrumbs={[
          { label: "Query Management", path: "/query-management" },
          { label: "My Queries" },
        ]}
        title="My Queries"
        subtitle="View and respond to queries sent to you by the administration."
        fallbackPath="/query-management"
        backLabel="Back to Query Management"
      />

      {/* Filters */}
      <div className="mb-6 dashboard-enter dashboard-enter-delay-1">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm smooth-transition"
        >
          <option value="">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="RESOLVED">Resolved</option>
        </select>
      </div>

      {/* Queries Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 dashboard-enter dashboard-enter-delay-2">
        {queries.length === 0 ? (
          <div className="md:col-span-2 lg:col-span-3 rounded-xl border-2 border-dashed border-gray-300 bg-white p-12 text-center dashboard-fade-in">
            <FaClock className="mx-auto mb-4 text-4xl text-gray-300" />
            <p className="text-lg font-medium text-gray-700">
              No queries at the moment
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Queries assigned to you will appear here.
            </p>
          </div>
        ) : (
          queries.map((query, index) => (
            <div
              key={query.id}
              className={`stagger-item rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition dashboard-card-hover`}
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    {query.subject}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {query.category}
                  </p>
                </div>
                {!query.isRead && (
                  <div className="ml-2 h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
                )}
              </div>

              {/* Description */}
              <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                {query.description}
              </p>

              {/* Badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium smooth-transition ${getPriorityColor(
                    query.priority
                  )}`}
                >
                  {query.priority}
                </span>
                <span
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium smooth-transition ${getStatusColor(
                    query.status
                  )}`}
                >
                  {query.status}
                </span>
              </div>

              {/* Timeline */}
              <div className="mt-4 space-y-2 text-xs text-gray-500">
                <p>Assigned: {formatDate(query.assignedAt)}</p>
                {query.deadline && (
                  <p>Deadline: {formatDate(query.deadline)}</p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenDetails(query)}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition dashboard-button-hover"
                >
                  View Details
                </button>
                {query.status !== "RESOLVED" && !query.hasResponded && (
                  <button
                    onClick={() => {
                      setSelectedQuery(query);
                      handleOpenResponseModal();
                    }}
                    className="flex-1 rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition dashboard-button-hover"
                  >
                    Respond
                  </button>
                )}
                {query.hasResponded && (
                  <div className="flex-1 rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-700 text-center">
                    ✓ Responded
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Query Details Modal */}
      <InviteModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedQuery(null);
        }}
      >
        {isLoadingDetails ? (
          <div className="p-8 text-center">Loading...</div>
        ) : selectedQuery ? (
          <div className="my-8 font-Outfit">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedQuery.subject}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {selectedQuery.category}
              </p>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-600">Priority</p>
                <p className="mt-1 font-medium text-gray-900">
                  {selectedQuery.priority}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-600">Status</p>
                <p className="mt-1 font-medium text-gray-900">
                  {selectedQuery.status}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <p className="mb-2 text-sm font-medium text-gray-700">
                Description
              </p>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                {selectedQuery.description}
              </p>
            </div>

            {/* Timeline */}
            <div className="mb-6 space-y-2 text-sm text-gray-600">
              <p>
                <span className="font-medium">Assigned:</span>{" "}
                {formatDate(selectedQuery.assignedAt)}
              </p>
              {selectedQuery.deadline && (
                <p>
                  <span className="font-medium">Deadline:</span>{" "}
                  {formatDate(selectedQuery.deadline)}
                </p>
              )}
            </div>

            {/* Previous Responses */}
            {selectedQuery.responses && selectedQuery.responses.length > 0 && (
              <div className="mb-6">
                <p className="mb-3 text-sm font-medium text-gray-700">
                  Your Responses
                </p>
                <div className="space-y-3 max-h-40 overflow-y-auto">
                  {selectedQuery.responses.map((resp, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <p className="text-xs text-gray-500">
                        {formatDate(resp.submittedAt)}
                      </p>
                      <p className="mt-1 text-sm text-gray-700">
                        {resp.responseText}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedQuery(null);
                }}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 transition dashboard-button-hover"
              >
                Close
              </button>
              {selectedQuery.status !== "RESOLVED" && !selectedQuery.hasResponded && (
                <button
                  onClick={handleOpenResponseModal}
                  className="flex-1 rounded-lg bg-[#1234CD] px-4 py-2 font-medium text-white hover:bg-blue-700 transition dashboard-button-hover"
                >
                  Add Response
                </button>
              )}
            </div>
          </div>
        ) : null}
      </InviteModal>

      {/* Response Modal */}
      <InviteModal
        isOpen={isResponseModalOpen}
        onClose={() => setIsResponseModalOpen(false)}
      >
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Submit Response</p>
          <p className="mt-2 text-sm text-gray-600">
            {selectedQuery?.subject}
          </p>

          <form className="my-8 space-y-4" onSubmit={handleSubmitResponse}>
            <div>
              <label className="mb-2 block text-sm font-medium text-black">
                Your Response <span className="text-red-500">*</span>
              </label>
              <textarea
                value={responseForm.responseText}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    responseText: e.target.value,
                  })
                }
                className="min-h-40 w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="Enter your response here..."
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setIsResponseModalOpen(false)}
                className="flex-1 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingResponse || !responseForm.responseText.trim()}
                className="flex-1 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:opacity-60 dashboard-button-hover"
              >
                {isSubmittingResponse ? "Submitting..." : "Submit Response"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      {/* Success Modal */}
      <SuccessModal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)}>
        <div className="flex flex-col items-center gap-4">
          <FaCheckCircle className="text-4xl text-green-600" />
          <p className="text-lg font-semibold text-green-600">
            {successMessage}
          </p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default EmployeeQueries;
